import { useState, useEffect, useRef, useCallback } from "react";
import { NotebookPen, X } from "lucide-react";
import { toast } from "sonner";
import { callsAPI } from "../../services/api";
import { formatPhoneNumber } from "../../lib/utils";
import NoteTemplateChips from "./NoteTemplateChips";
import { appendNoteTemplate } from "../../lib/callNoteTemplates";

const NAVY = "#003366";
const YELLOW = "#FFDA00";

// Quick post-call note prompt. Pops when a DESK-PHONE call ends (the app's own
// calls already open the richer PostCallDocumentationModal). Listens for the
// window event dispatched by DeskPhoneCallCompanion so it stays decoupled.
export default function AutoCallNotePrompt() {
  const [ended, setEnded] = useState(null);   // { phone, company, seconds, call_sid }
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const onEnded = (e) => {
      const d = e.detail || {};
      if (!d.phone) return;             // need a number to key the note
      setText("");
      setEnded(d);
    };
    window.addEventListener("avida:deskcall-ended", onEnded);
    return () => window.removeEventListener("avida:deskcall-ended", onEnded);
  }, []);

  // Auto-dismiss if ignored for 45s so it never lingers.
  useEffect(() => {
    if (!ended) return;
    timerRef.current = setTimeout(() => setEnded(null), 45000);
    return () => clearTimeout(timerRef.current);
  }, [ended]);

  const close = useCallback(() => { setEnded(null); setText(""); }, []);

  const save = async () => {
    const t = text.trim();
    if (!t) { close(); return; }
    setBusy(true);
    try {
      await callsAPI.addCallNote(ended.phone, t);
      // Mirror the app-call behavior: auto-log to the matched client's log.
      try {
        const token = localStorage.getItem("token");
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/healthcare/relationship-log/auto-log-call`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ phone: ended.phone, direction: "inbound", duration_sec: ended.seconds || 0, call_sid: ended.call_sid || null }),
        }).catch(() => {});
      } catch { /* noop */ }
      toast.success("Note saved to caller history");
      close();
    } catch {
      toast.error("Couldn't save the note. Try again.");
    } finally { setBusy(false); }
  };

  if (!ended) return null;
  const who = ended.company || formatPhoneNumber(ended.phone) || "caller";

  return (
    <div data-testid="auto-call-note-prompt" className="fixed left-4 bottom-4 z-[58] w-[320px] rounded-2xl shadow-2xl overflow-hidden bg-white border border-slate-200">
      <div className="px-4 py-2.5 flex items-center justify-between" style={{ background: NAVY }}>
        <div className="flex items-center gap-2 text-white">
          <NotebookPen className="w-4 h-4" style={{ color: YELLOW }} />
          <span className="text-[12px] font-bold">Quick note · desk call ended</span>
        </div>
        <button data-testid="auto-note-dismiss" onClick={close} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      <div className="p-3">
        <p className="text-[11px] text-slate-500 mb-2 truncate">With <b className="text-slate-700">{who}</b> · {formatPhoneNumber(ended.phone)}</p>
        <NoteTemplateChips className="mb-2" onPick={(t) => setText((d) => appendNoteTemplate(d, t))} />
        <textarea
          autoFocus data-testid="auto-note-text" value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save(); }}
          rows={3} placeholder="What was this call about? (⌘/Ctrl+Enter to save)"
          className="w-full px-2.5 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:border-[#003366] resize-none"
        />
        <div className="flex items-center justify-end gap-2 mt-2">
          <button data-testid="auto-note-skip" onClick={close} className="h-9 px-3 rounded-lg text-[12px] font-semibold text-slate-500 hover:bg-slate-100">Skip</button>
          <button data-testid="auto-note-save" onClick={save} disabled={busy || !text.trim()}
            className="h-9 px-4 rounded-lg text-[12px] font-bold disabled:opacity-50" style={{ background: NAVY, color: YELLOW }}>
            {busy ? "Saving…" : "Save note"}
          </button>
        </div>
      </div>
    </div>
  );
}
